import { c as _c } from "react/compiler-runtime";
import type { InvestigationSourceData, SourceCitationEvidence } from '@onefootprint/request-types/dashboard';
import { Button } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import { formatSourceDate } from '../../../../utils/source-display';
import CitationHighlight from '../citation-highlight/citation-highlight';
import CitedPassages, { PassagePager, usePagedIndex } from '../cited-passages/cited-passages';
const formatJsonPreview = (value: unknown, empty: string) => {
  if (value === null || value === undefined) return empty;
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
};
const JsonPreview = t0 => {
  const $ = _c(8);
  const {
    value
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  let t2;
  let t3;
  if ($[1] !== t || $[2] !== value) {
    const empty = t(_temp);
    t2 = "max-h-72 overflow-auto rounded-default border border-tertiary bg-secondary p-3 text-snippet-2 text-primary";
    t3 = formatJsonPreview(value, empty);
    $[1] = t;
    $[2] = value;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <pre className={t2}>{t3}</pre>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const RestrictedOutput = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t0);
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp2);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="px-3 py-3"><div className="rounded-default border border-tertiary bg-secondary px-3 py-2 text-body-3 text-secondary">{t1}</div></div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
type IntegrationExecutionData = Extract<InvestigationSourceData, {
  kind: 'integration_execution';
}>['data'];

// A merged chip can carry several cites into one vendor payload, so its highlights page the way web
// passages do.
const IntegrationExecutionBody = t0 => {
  const $ = _c(42);
  const {
    execution,
    evidences
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  const [index, setIndex] = usePagedIndex(evidences);
  const safeIndex = Math.min(index, Math.max(0, evidences.length - 1));
  const current = evidences[safeIndex];
  let t2;
  if ($[1] !== t) {
    t2 = t(_temp3);
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="text-body-3 text-tertiary">{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = execution.isError ? "text-error" : "text-success";
  let t5;
  if ($[5] !== t4) {
    t5 = cx("text-body-3", t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== execution.isError || $[8] !== t) {
    t6 = execution.isError ? t(_temp4) : t(_temp5);
    $[7] = execution.isError;
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t3 || $[14] !== t7) {
    t8 = <div className="flex min-w-0 flex-col gap-0.5">{t3}{t7}</div>;
    $[13] = t3;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t(_temp6);
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <span className="text-body-3 text-tertiary">{t9}</span>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== execution.observedAt) {
    t11 = formatSourceDate(execution.observedAt);
    $[20] = execution.observedAt;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = <span className="truncate text-body-3 text-secondary">{t11}</span>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t12) {
    t13 = <div className="flex min-w-0 flex-col gap-0.5">{t10}{t12}</div>;
    $[24] = t10;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== evidences.length || $[28] !== safeIndex || $[29] !== setIndex) {
    t14 = evidences.length > 1 && <div className="flex justify-end"><PassagePager index={safeIndex} total={evidences.length} onIndex={setIndex} /></div>;
    $[27] = evidences.length;
    $[28] = safeIndex;
    $[29] = setIndex;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== current || $[32] !== execution.output || $[33] !== t) {
    t15 = current ? <CitationHighlight evidence={current} output={execution.output} /> : <><span className="text-body-3 text-tertiary">{t(_temp7)}</span><JsonPreview value={execution.output} /></>;
    $[31] = current;
    $[32] = execution.output;
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t14 || $[36] !== t15) {
    t16 = <div className="flex min-w-0 flex-col gap-1">{t14}{t15}</div>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t13 || $[39] !== t16 || $[40] !== t8) {
    t17 = <div className="flex flex-col gap-4 px-3 py-3">{t8}{t13}{t16}</div>;
    $[38] = t13;
    $[39] = t16;
    $[40] = t8;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
};

// Just the action: the row or popover header above already names the document.
const VaultDocumentBody = t0 => {
  const $ = _c(10);
  const {
    documentIdentifier,
    onOpenDocument
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "sourcesDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t1);
  const t2 = !onOpenDocument;
  let t3;
  if ($[1] !== documentIdentifier || $[2] !== onOpenDocument) {
    t3 = () => onOpenDocument?.(documentIdentifier);
    $[1] = documentIdentifier;
    $[2] = onOpenDocument;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t(_temp8);
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <div className="flex min-w-0 flex-col px-3 py-3"><Button size="compact" variant="secondary" disabled={t2} onClick={t3}>{t4}</Button></div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const SourceDetail = t0 => {
  const $ = _c(12);
  const {
    data,
    documentIdentifier,
    evidences: t1,
    onOpenDocument
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const evidences = t2;
  if (documentIdentifier) {
    let t3;
    if ($[2] !== documentIdentifier || $[3] !== onOpenDocument) {
      t3 = <VaultDocumentBody documentIdentifier={documentIdentifier} onOpenDocument={onOpenDocument} />;
      $[2] = documentIdentifier;
      $[3] = onOpenDocument;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (!data) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <RestrictedOutput />;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  switch (data.kind) {
    case "integration_execution":
      {
        let t3;
        if ($[6] !== data.data || $[7] !== evidences) {
          t3 = <IntegrationExecutionBody execution={data.data} evidences={evidences} />;
          $[6] = data.data;
          $[7] = evidences;
          $[8] = t3;
        } else {
          t3 = $[8];
        }
        return t3;
      }
    case "web_fetch":
      {
        let t3;
        if ($[9] !== data.data.paragraphs || $[10] !== evidences) {
          t3 = <CitedPassages evidences={evidences} paragraphs={data.data.paragraphs} />;
          $[9] = data.data.paragraphs;
          $[10] = evidences;
          $[11] = t3;
        } else {
          t3 = $[11];
        }
        return t3;
      }
    default:
      {
        const exhaustive = data;
        return exhaustive;
      }
  }
};
export default SourceDetail;
function _temp($) {
  return $.noOutput;
}
function _temp2($) {
  return $.restrictedDescription;
}
function _temp3($) {
  return $.vendorResult;
}
function _temp4($_0) {
  return $_0.vendorError;
}
function _temp5($_1) {
  return $_1.vendorSuccess;
}
function _temp6($_2) {
  return $_2.observed;
}
function _temp7($_3) {
  return $_3.decryptedOutput;
}
function _temp8($) {
  return $.openDocument;
}